/**
 * Semantic icon registry — maps meaning to Iconify Tailwind classes.
 * Iconify IntelliSense shows icon previews on the string values.
 *
 * Usage:
 *   import { appIcons, type AppIconName } from '../../../../types/icons'
 *   <AppIcon name="delete" />
 *
 * To add a new icon:
 *   1. Find the icon on https://icon-sets.iconify.design/
 *   2. Add a semantic key + the icon-[{set}--{name}] class below
 *   3. If the set isn't enabled yet, add it to addIconSelectors() in tailwind.config.ts
 */
export const appIcons = {
  // Actions
  add: 'icon-[lucide--plus]',
  delete: 'icon-[lucide--trash-2]',
  edit: 'icon-[lucide--pencil]',
  save: 'icon-[lucide--save]',
  cancel: 'icon-[lucide--x]',
  close: 'icon-[lucide--x]',
  copy: 'icon-[lucide--copy]',
  paste: 'icon-[lucide--clipboard-paste]',
  download: 'icon-[lucide--download]',
  upload: 'icon-[lucide--upload]',
  send: 'icon-[lucide--send]',
  refresh: 'icon-[lucide--refresh-cw]',
  undo: 'icon-[lucide--undo-2]',
  redo: 'icon-[lucide--redo-2]',
  search: 'icon-[lucide--search]',
  filter: 'icon-[lucide--filter]',
  filterOff: 'icon-[lucide--filter-x]',
  play: 'icon-[lucide--play]',
  stop: 'icon-[lucide--square]',
  link: 'icon-[lucide--link]',
  externalLink: 'icon-[lucide--external-link]',
  merge: 'icon-[lucide--git-merge]',
  share: 'icon-[lucide--share-2]',
  print: 'icon-[lucide--printer]',
  reset: 'icon-[lucide--refresh-ccw]',

  // Navigation
  home: 'icon-[lucide--house]',
  arrowLeft: 'icon-[lucide--arrow-left]',
  arrowRight: 'icon-[lucide--arrow-right]',
  arrowUp: 'icon-[lucide--arrow-up]',
  arrowDown: 'icon-[lucide--arrow-down]',
  chevronLeft: 'icon-[lucide--chevron-left]',
  chevronRight: 'icon-[lucide--chevron-right]',
  chevronUp: 'icon-[lucide--chevron-up]',
  chevronDown: 'icon-[lucide--chevron-down]',
  menu: 'icon-[lucide--menu]',
  signIn: 'icon-[lucide--log-in]',
  login: 'icon-[lucide--log-in]',
  signOut: 'icon-[lucide--log-out]',
  logout: 'icon-[lucide--log-out]',

  // Status & Feedback
  check: 'icon-[lucide--check]',
  checkCircle: 'icon-[lucide--circle-check]',
  checkSquare: 'icon-[lucide--square-check]',
  warning: 'icon-[lucide--triangle-alert]',
  error: 'icon-[lucide--circle-x]',
  closeCircle: 'icon-[lucide--circle-x]',
  pointer: 'icon-[lucide--mouse-pointer-click]',
  info: 'icon-[lucide--info]',
  help: 'icon-[lucide--circle-question-mark]',
  bug: 'icon-[lucide--bug]',
  verified: 'icon-[lucide--badge-check]',
  star: 'icon-[lucide--star]',
  lightbulb: 'icon-[lucide--lightbulb]',
  circleFill: 'icon-[lucide--circle]',

  // Objects & Concepts
  file: 'icon-[lucide--file]',
  fileEdit: 'icon-[lucide--file-pen]',
  filePdf: 'icon-[lucide--file-text]',
  fileExcel: 'icon-[lucide--file-spreadsheet]',
  archive: 'icon-[lucide--file-archive]',
  folder: 'icon-[lucide--folder]',
  image: 'icon-[lucide--image]',
  images: 'icon-[lucide--images]',
  calendar: 'icon-[lucide--calendar]',
  calendarClock: 'icon-[lucide--calendar-clock]',
  calendarPlus: 'icon-[lucide--calendar-plus]',
  clock: 'icon-[lucide--clock]',
  stopwatch: 'icon-[lucide--timer]',
  hourglass: 'icon-[lucide--hourglass]',
  map: 'icon-[lucide--map]',
  car: 'icon-[lucide--car]',
  mapMarker: 'icon-[lucide--map-pin]',
  mapPin: 'icon-[lucide--map-pin]',
  globe: 'icon-[lucide--globe]',
  phone: 'icon-[lucide--phone]',
  envelope: 'icon-[lucide--mail]',
  whatsapp: 'icon-[lucide--message-circle]',
  inbox: 'icon-[lucide--inbox]',
  bell: 'icon-[lucide--bell]',
  clipboard: 'icon-[lucide--clipboard-list]',

  // People
  user: 'icon-[lucide--user]',
  userPlus: 'icon-[lucide--user-plus]',
  users: 'icon-[lucide--users]',

  // Data & Charts
  chartBar: 'icon-[lucide--bar-chart-3]',
  chartLine: 'icon-[lucide--line-chart]',
  chartPie: 'icon-[lucide--pie-chart]',
  database: 'icon-[lucide--database]',
  table: 'icon-[lucide--table]',
  list: 'icon-[lucide--list]',

  // Settings & System
  settings: 'icon-[lucide--settings]',
  sliders: 'icon-[lucide--sliders-horizontal]',
  layers: 'icon-[lucide--layers]',
  shield: 'icon-[lucide--shield]',
  server: 'icon-[lucide--server]',
  wifi: 'icon-[lucide--wifi]',
  palette: 'icon-[lucide--palette]',
  wrench: 'icon-[lucide--wrench]',

  // Entities
  aanvraag: 'icon-[lucide--file-plus]',
  locatie: 'icon-[lucide--building]',
  offerte: 'icon-[lucide-lab--euro-square]',
  bundel: 'icon-[lucide--layers]',
  factuur: 'icon-[lucide--receipt-euro]',
  rapport: 'icon-[lucide--clipboard-check]',
  constatering: 'icon-[lucide--search-alert]',
  opdrachtgever: 'icon-[lucide--file-user]',

  // Domain-specific
  building: 'icon-[lucide--building-2]',
  calculator: 'icon-[lucide--calculator]',
  euro: 'icon-[lucide--euro]',
  bolt: 'icon-[lucide--zap]',
  sitemap: 'icon-[lucide--network]',
  box: 'icon-[lucide--box]',
  alignLeft: 'icon-[lucide--align-left]',
  braces: 'icon-[lucide--braces]',
  minimize: 'icon-[lucide--minimize-2]',
  expand: 'icon-[lucide--maximize-2]',
  eye: 'icon-[lucide--eye]',
  view: 'icon-[lucide--eye]',
  viewOff: 'icon-[lucide--eye-off]',
  plusCircle: 'icon-[lucide--circle-plus]',
  moneyBill: 'icon-[lucide--banknote]',
  ellipsisH: 'icon-[lucide--ellipsis]',
  ellipsisV: 'icon-[lucide--ellipsis-vertical]',
  move: 'icon-[lucide--move]',
  scissors: 'icon-[lucide--scissors]',

  // Image editing
  crop: 'icon-[lucide--crop]',
  rotate: 'icon-[lucide--rotate-ccw]',
  flipHorizontal: 'icon-[lucide--flip-horizontal]',
  flipVertical: 'icon-[lucide--flip-vertical]',
  brush: 'icon-[lucide--brush]',
  undo2: 'icon-[lucide--corner-up-left]',
  redo2: 'icon-[lucide--corner-up-right]',

  // Sync/Cloud (PowerSync status)
  cloud: 'icon-[lucide--cloud]',
  cloudOff: 'icon-[lucide--cloud-off]',
  cloudAlert: 'icon-[lucide--cloud-alert]',
  cloudPause: 'icon-[lucide--circle-pause]',
  cloudUpload: 'icon-[lucide--cloud-upload]',
  cloudDownload: 'icon-[lucide--cloud-download]',
  cloudSync: 'icon-[lucide--cloud-cog]',
  cloudCheck: 'icon-[lucide--cloud-check]',

  // Loading
  spinner: 'icon-[lucide--loader-circle]',
  sync: 'icon-[lucide--refresh-cw]',

  // Misc
  minus: 'icon-[lucide--minus]',
  sun: 'icon-[lucide--sun]',
  moon: 'icon-[lucide--moon]',
  pause: 'icon-[lucide--pause]',
  cog: 'icon-[lucide--cog]',
  minusCircle: 'icon-[lucide--circle-minus]',
  eyeOpen: 'icon-[lucide--eye]',

  fileCheck: 'icon-[lucide--file-check]',

  // Image-specific (formerly oh-vue-icons)
  imageAdd: 'icon-[lucide--image-plus]',
  searchOff: 'icon-[lucide--search-x]',
} as const

export type AppIconName = keyof typeof appIcons
